import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { IntakeSettings } from "../../../models/intake";
import { RouterAddress } from "../../../constants/RouterAddress";
import {
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

type Props = {
  visible: boolean;
  onHide: () => void;
  homeId: string;
  homeName: string;
};

const defaultSettings = (homeId: string): IntakeSettings => ({
  homeId,
  enabled: false,
  allowUploads: true,
  allowFax: false,
  title: "Resident Screening",
  instructions: "",
});

const IntakeFormSettingsModal: FC<Props> = ({ visible, onHide, homeId, homeName }) => {
  const { backendAPIStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const [settings, setSettings] = useState<IntakeSettings>(defaultSettings(homeId));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!visible || !homeId) return;
    setLoading(true);
    setError("");
    setMessage("");
    void backendAPIStore.init()
      .then(() => backendAPIStore.getIntakeSettings(homeId))
      .then((settingsResult) => {
        const loadedSettings = { ...defaultSettings(homeId), ...settingsResult };
        const suggestedDetails = {
          title: `${homeName} Resident Screening`,
          instructions: "Please complete this screening form and upload any available documents. We will review the information and contact you if anything else is needed.",
        };
        setSettings({
          ...loadedSettings,
          title: loadedSettings.title?.trim() || suggestedDetails.title,
          instructions: loadedSettings.instructions?.trim() || suggestedDetails.instructions,
        });
      })
      .catch((requestError: any) => setError(requestError?.message || "Unable to load request settings."))
      .finally(() => setLoading(false));
  }, [backendAPIStore, homeId, homeName, visible]);

  const intakeUrl = settings.publicToken
    ? `${window.location.origin}${RouterAddress.PUBLIC_INTAKE.replace(":token", settings.publicToken)}`
    : "";

  const save = async (regenerateToken: boolean = false) => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await backendAPIStore.init();
      const result = await backendAPIStore.updateIntakeSettings({ ...settings, homeId, allowUploads: true, allowFax: false, regenerateToken });
      setSettings(result);
      if (regenerateToken) {
        setMessage("A new request link was generated.");
      } else {
        toast.current?.show({ severity: "success", summary: "Settings saved", detail: "Resident screening and Home Profile settings were saved.", life: 3000 });
        if (!result.publicToken) {
          setMessage("Settings saved. Generate a public link before closing this dialog.");
        }
      }
    } catch (requestError: any) {
      setError(requestError?.message || "Unable to save request settings.");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async () => {
    if (!intakeUrl) return;
    await navigator.clipboard.writeText(intakeUrl);
    setMessage("Request link copied.");
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
      visible={visible}
      modal
      header={renderSurfaceHeader("Resident Screening", `Manage the public resident screening for ${homeName}.`, "pi pi-file-edit")}
      style={appDialogStyle}
      headerStyle={appDialogHeaderStyle}
      contentStyle={appDialogContentStyle}
      onHide={onHide}
      footer={(
        <div className="flex justify-content-end gap-2">
          <Button label="Close" severity="secondary" outlined onClick={onHide} disabled={saving} />
          <Button label="Save Settings" icon="pi pi-check" severity="warning" rounded onClick={() => void save(false)} loading={saving} />
        </div>
      )}
    >
      {loading ? <div className="flex justify-content-center p-5"><ProgressSpinner /></div> : (
        <div className="flex flex-column gap-4">
          {error ? <Message severity="error" text={error} /> : null}
          {message ? <Message severity="success" text={message} /> : null}
          <div className="grid">
            <div className="col-12 md:col-6"><div style={{ ...appAccentPanelStyle, padding: "1rem" }} className="flex flex-column gap-2 h-full">
              <div className="flex align-items-center justify-content-between gap-3">
                <div className="text-lg font-semibold">Public screening</div>
                <div className="flex align-items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-semibold text-color-secondary">{settings.enabled ? "ON" : "OFF"}</span>
                  <InputSwitch checked={settings.enabled} onChange={(event) => setSettings({ ...settings, enabled: !!event.value })} />
                </div>
              </div>
              <div className="text-color-secondary">Let case workers, hospitals, families, and referral partners submit resident information and documents.</div>
              {!settings.enabled ? <Message severity="info" className="m-0" text="The form is currently turned off. Turn on the switch when you are ready to share it." /> : null}
            </div></div>
            <div className="col-12 md:col-6"><div style={{ ...appFormSectionStyle, padding: "1rem" }} className="flex flex-column gap-2 h-full">
              <div className="text-lg font-semibold">Share the screening form</div>
              <div className="surface-100 border-round px-2 py-2 font-medium text-sm" style={{ overflowWrap: "anywhere" }}>{intakeUrl || "Creating public link..."}</div>
              <div className="flex flex-wrap gap-2">
                <Button label="Copy Link" icon="pi pi-copy" severity="warning" outlined rounded disabled={!intakeUrl} onClick={() => void copyUrl()} />
                <Button label="View Screening Form" icon="pi pi-external-link" severity="warning" outlined rounded aria-label="Open public resident screening in a new tab" disabled={!intakeUrl} onClick={() => window.open(intakeUrl, "_blank", "noopener,noreferrer")} />
                <Button label="Generate New Link" icon="pi pi-refresh" severity="secondary" outlined rounded disabled={!settings.publicToken || saving} onClick={() => void save(true)} />
              </div>
            </div></div>
          </div>
          <div style={appAccentPanelStyle} className="flex flex-column gap-4">
            <div className="flex flex-column gap-1">
              <div className="text-lg font-semibold">Screening Form Details</div>
              <div className="text-color-secondary">Add a title and any short instructions for referral partners.</div>
            </div>
            <span className="p-float-label">
              <InputText id="intake-title" className="w-full" value={settings.title} maxLength={120} onChange={(event) => setSettings({ ...settings, title: event.target.value })} />
              <label htmlFor="intake-title">Screening title</label>
            </span>
            <span className="p-float-label">
              <InputTextarea id="intake-instructions" className="w-full" rows={4} maxLength={1000} value={settings.instructions} onChange={(event) => setSettings({ ...settings, instructions: event.target.value })} />
              <label htmlFor="intake-instructions">Case manager and referral agent instructions (optional)</label>
            </span>
            <div className="flex flex-column gap-3">
              <div className="text-lg font-semibold">Home Pharmacy Details</div>
              <div className="grid">
                <div className="col-12 md:col-6"><span className="p-float-label"><InputText id="intake-pharmacy-name" className="w-full" maxLength={150} value={settings.pharmacyName || ""} onChange={(event) => setSettings({ ...settings, pharmacyName: event.target.value })} /><label htmlFor="intake-pharmacy-name">Home pharmacy name</label></span></div>
                <div className="col-12 md:col-6"><span className="p-float-label"><InputText id="intake-pharmacy-phone" className="w-full" maxLength={30} value={settings.pharmacyPhoneNumber || ""} onChange={(event) => setSettings({ ...settings, pharmacyPhoneNumber: event.target.value })} /><label htmlFor="intake-pharmacy-phone">Home pharmacy phone number</label></span></div>
                <div className="col-12"><span className="p-float-label"><InputTextarea id="intake-pharmacy-address" className="w-full" rows={3} maxLength={500} value={settings.pharmacyAddress || ""} onChange={(event) => setSettings({ ...settings, pharmacyAddress: event.target.value })} /><label htmlFor="intake-pharmacy-address">Home pharmacy address</label></span></div>
              </div>
            </div>
            <small className="text-color-secondary">These details are shown on the public screening form when the referral partner selects No for “Does the client have enough medication for the next 30 days?”</small>
          </div>
        </div>
      )}
      </Dialog>
    </>
  );
};

export default IntakeFormSettingsModal;
